import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Download, RefreshCw, ListChecks, Gavel } from "lucide-react";
import { API, api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/** AI-generated summary of the pleadings and annexures for the tribunal; refreshes itself while generating. */
export const CaseSummary = ({ caseId, onOrderRecorded }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = () => api.get(`/cases/${caseId}/summary`).then(({ data }) => setS(data)).catch(() => {});
  useEffect(() => { load(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const pending = s?.status === "generating" || (s?.both_filed && !s?.available && s?.status !== "failed") || s?.stale;
    if (!pending) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [s]); // eslint-disable-line react-hooks/exhaustive-deps

  const regenerate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/cases/${caseId}/summary/regenerate`); setS(data); toast.success("Summary regenerated"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const openDraft = async () => {
    try { const { data } = await api.get(`/cases/${caseId}/summary/draft-issues`); setDraft(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const recordDraft = async () => {
    setBusy(true);
    try {
      await api.post("/orders", { case_id: caseId, kind: draft.kind, number: draft.number || null, title: draft.title,
        issued_on: new Date().toISOString().slice(0, 10), body: draft.body });
      toast.success(`${draft.number} ${draft.title} recorded — PDF saved to Documents`);
      setDraft(null);
      onOrderRecorded?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  const sum = s.summary;
  return (
    <section className="border border-border bg-card" data-testid="case-summary-section">
      <div className="px-6 py-4 border-b border-border flex flex-wrap items-center gap-3">
        <Sparkles className="h-4 w-4 text-blue-900 dark:text-blue-400" />
        <p className="label-caps">Case summary</p>
        {s.available && <span className="text-[11px] text-muted-foreground" data-testid="case-summary-meta">from {s.versions} · {fmtDateTime(s.generated_at)}{s.stale || s.status === "generating" ? " · updating…" : ""}</span>}
        <div className="ml-auto flex items-center gap-4">
          {s.available && (
            <a href={`${API}/cases/${caseId}/summary.pdf`} target="_blank" rel="noreferrer" data-testid="case-summary-pdf"
              className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
              <Download className="h-3.5 w-3.5" /> PDF
            </a>
          )}
          {s.available && (
            <button onClick={openDraft} data-testid="draft-issues-button"
              className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
              <ListChecks className="h-3.5 w-3.5" /> Draft issues order
            </button>
          )}
          {s.both_filed && (
            <button onClick={regenerate} disabled={busy || s.status === "generating"} data-testid="case-summary-regenerate"
              className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors duration-200 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${busy || s.status === "generating" ? "animate-spin" : ""}`} /> Regenerate
            </button>
          )}
        </div>
      </div>
      <div className="px-6 py-5 text-sm space-y-4">
        {!s.both_filed && <p className="text-muted-foreground" data-testid="case-summary-waiting">The summary is prepared automatically once both the Statement of Claim and the Statement of Defence are on record.</p>}
        {s.both_filed && !s.available && s.status !== "failed" && <p className="text-muted-foreground animate-pulse" data-testid="case-summary-generating">Reading the pleadings and scanned annexures, preparing the summary…</p>}
        {s.status === "failed" && !s.available && <p className="text-red-800 dark:text-red-400" data-testid="case-summary-failed">The summary could not be generated ({s.error}). Use Regenerate to try again.</p>}
        {s.available && (
          <>
            <Block n="1" title="Nature of the dispute" testId="summary-dispute">{sum.dispute}</Block>
            <div data-testid="summary-issues">
              <p className="font-semibold">2. Key issues in contention</p>
              <ol className="mt-1 space-y-1 list-decimal pl-5">{sum.issues.map((i, k) => <li key={k}>{i}</li>)}</ol>
            </div>
            <Block n="3" title="Relief sought by the claimant" testId="summary-relief">{sum.relief_claimant}</Block>
            <Block n="4" title="Defence taken by the respondent" testId="summary-defence">{sum.defence_respondent}</Block>
            {sum.counter_claim && sum.counter_claim.toLowerCase() !== "none" && <Block n="5" title="Counter-claim" testId="summary-counter">{sum.counter_claim}</Block>}
            {s.annexures?.length > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="summary-annexures">
                Also read: {s.annexures.map((a) => `${a.title} (${a.side})`).join("; ")}
              </p>
            )}
            <p className="text-xs italic text-muted-foreground border-t border-border pt-3" data-testid="summary-disclaimer">{s.disclaimer}</p>
          </>
        )}
        {draft && (
          <div className="border-2 border-blue-900 p-5 space-y-3 bg-background" data-testid="draft-issues-panel">
            <p className="label-caps flex items-center gap-2"><Gavel className="h-3.5 w-3.5" /> Draft procedural order — edit, then record</p>
            <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3">
              <Input value={draft.number} onChange={(e) => setDraft({ ...draft, number: e.target.value })} className="rounded-none h-10" data-testid="draft-number-input" />
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="rounded-none h-10" data-testid="draft-title-input" />
            </div>
            <Textarea rows={12} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              className="rounded-none font-serif text-[15px] leading-relaxed" data-testid="draft-body-input" />
            <div className="flex gap-3">
              <Button onClick={recordDraft} disabled={busy || !draft.body.trim()} data-testid="record-issues-order-button"
                className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10 px-5">
                {busy ? "Recording…" : "Record as procedural order"}
              </Button>
              <Button variant="ghost" onClick={() => setDraft(null)} data-testid="draft-cancel-button" className="rounded-none uppercase tracking-widest text-xs h-10">Discard</Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const Block = ({ n, title, children, testId }) => (
  <div data-testid={testId}>
    <p className="font-semibold">{n}. {title}</p>
    <p className="mt-1 whitespace-pre-wrap leading-relaxed">{children}</p>
  </div>
);
